import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "ticketino-api-client";
import { UpdateTicketAddress, GetAddressOfOrganizer, UpdateBuyerAddress } from "../utils/api";
import { useLanguage } from "../hooks/useLanguage";
import { useOrder } from "../hooks/useOrder";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Loading from "./components/Loading";

const Information = () => {
    const [regularToken, setRegularToken] = useState(null);
    const [bookingType, setBookingType] = useState(null);
    const [resources, setResources] = useState({});

    const [formError, setFormError] = useState("");

    const { language } = useLanguage();

    const organizerId = localStorage.getItem("loggedInOrganizerId");

    const [buyerAddress, setBuyerAddress] = useState({
        organizerId: 0,
        contactFirstName: "",
        contactLastName: "",
        contactPhone: "",
        birthdayName: "",
        birthdayAge: "",
        schoolGradeLevel: "",
        groupDescription: "",
        remark: "",
    });

    //for birthday only defined in the addOns page
    const hasBlackTapMilkshake = localStorage.getItem("hasBlackTapMilkshake");

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const order = useOrder(baseUrl);

    // Helper function to convert booking type to German
    const getBookingTypeGerman = (bookingType) => {
        const bookingTypeMap = {
            'birthday': 'Kindergeburtstag',
            'schoolgroup': 'Schulgruppen',
            'groupvisit': 'Gruppenbesuch'
        };
        return bookingTypeMap[bookingType] || bookingType;
    };

    useEffect(() => {
        setRegularToken(localStorage.getItem("token"));
        setBookingType(localStorage.getItem("bookingType"));

        // Load saved buyer address from localStorage
        const savedBuyerAddress = localStorage.getItem("buyerAddress");
        if (savedBuyerAddress) {
            try {
                const parsedAddress = JSON.parse(savedBuyerAddress);
                setBuyerAddress(parsedAddress);
            } catch (error) {
                console.error("Error parsing saved buyer address:", error);
            }
        }
    }, []);

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const validateInputFields = () => {
        const genericError = resources?.ErrorPleaseFillMandatoryFields;

        if (!buyerAddress) return { valid: false, error: null };
        if (!buyerAddress.contactFirstName || buyerAddress.contactFirstName.length > 70) return { valid: false, error: genericError };
        if (!buyerAddress.contactLastName || buyerAddress.contactLastName.length > 70) return { valid: false, error: genericError };
        if (!buyerAddress.contactPhone || buyerAddress.contactPhone.length > 70) return { valid: false, error: genericError };

        if (bookingType === "birthday") {
            if (!buyerAddress.birthdayName || buyerAddress.birthdayName.length > 70) return { valid: false, error: genericError };
            if (!buyerAddress.birthdayAge || buyerAddress.birthdayAge.length > 70) return { valid: false, error: genericError };
        }

        if (bookingType === "schoolgroup") {
            if (!buyerAddress.schoolGradeLevel || buyerAddress.schoolGradeLevel.length > 70) return { valid: false, error: genericError };
        }

        if (bookingType === "groupvisit") {
            if (!buyerAddress.groupDescription || buyerAddress.groupDescription.length > 70) return { valid: false, error: genericError };
        }

        return { valid: true };
    }

    const fetchOrganizerAddress = async (buyerToken, organizerId) => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchOrganizerAddress");
            }
            const data = await GetAddressOfOrganizer(baseUrl, "buyerToken", buyerToken, organizerId);
            return data;
        } catch (error) {
            console.error('Error fetching organizer address:', error);
        }
    };

    const handleUpdateBuyerAddress = async (e) => {
        if (e) e.preventDefault();
        setFormError("");
        const validation = validateInputFields();
        if (!validation.valid) {
            setFormError(validation.error || "Please fill all mandatory fields correctly.");
            return;
        }
        if (!order?.tickets) {
            setFormError(resources?._SessionExpired);
            return;
        };

        try {
            // Save buyer address to localStorage before proceeding
            localStorage.setItem("buyerAddress", JSON.stringify(buyerAddress));

            const organizerAddress = await fetchOrganizerAddress(regularToken, organizerId);

            // Convert bookingType to German for saving
            const bookingTypeGerman = getBookingTypeGerman(bookingType);

            // Add bookingType to code10 (in German)
            organizerAddress.code1 = buyerAddress.contactFirstName;
            organizerAddress.code2 = buyerAddress.contactLastName;
            organizerAddress.code3 = buyerAddress.contactPhone;
            organizerAddress.code4 = buyerAddress.remark;
            organizerAddress.code5 = hasBlackTapMilkshake;
            organizerAddress.code6 = buyerAddress.birthdayName;
            organizerAddress.code7 = buyerAddress.birthdayAge;
            organizerAddress.code8 = buyerAddress.schoolGradeLevel;
            organizerAddress.code9 = bookingTypeGerman;

            // update address of order with logged in user
            await UpdateBuyerAddress(baseUrl, "token", regularToken, order.id, organizerAddress);

            for (const t of order.tickets) {
                const ticketAddressBody = {
                    option1: buyerAddress.contactFirstName,
                    option2: buyerAddress.contactLastName,
                    option3: buyerAddress.contactPhone,
                    option4: buyerAddress.remark,
                    option5: hasBlackTapMilkshake,
                    option6: buyerAddress.birthdayName,
                    option7: buyerAddress.birthdayAge,
                    option8: buyerAddress.schoolGradeLevel,
                    option9: bookingTypeGerman,

                };

                await UpdateTicketAddress(baseUrl, "token", regularToken, t.id, ticketAddressBody);
            }

            navigate(`/${language}/checkout`);
        } catch (error) {
            setFormError(`${resources?._Save} failed.`);
        }
    };

    const onInputChange = (e) => {
        setBuyerAddress({ ...buyerAddress, [e.target.name]: e.target.value });
    };

    return (
        <>
            <Header language={language} pagePath="information" pageTitle={resources?.InformationTitle}
                ticketCount={order?.tickets?.length || 0} />

            <div className="page-container">

                <div className="container wrapper">

                    {/* inputs */}
                    <div className="form-group row">
                        <label htmlFor="contactFirstName" className="col-md-12 col-form-label">{resources?.InformationContactFirstName} *</label>
                        <div className="col-md-12">
                            <input
                                type="text"
                                name="contactFirstName"
                                className="form-control"
                                value={buyerAddress?.contactFirstName || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="contactLastName" className="col-md-12 col-form-label">{resources?.InformationContactLastName} *</label>
                        <div className="col-md-12">
                            <input
                                type="text"
                                name="contactLastName"
                                className="form-control"
                                value={buyerAddress?.contactLastName || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="contactPhone" className="col-md-12 col-form-label">{resources?.InformationContactPhone} *</label>
                        <div className="col-md-12">
                            <input
                                type="text"
                                name="contactPhone"
                                className="form-control"
                                value={buyerAddress?.contactPhone || ""}
                                onChange={onInputChange}
                            />
                        </div>
                    </div>

                    {/* weitere angabe */}
                    <div className="row text-center mt-5 mb-5">
                        <div className="col-md-12">
                            <span className="title">{resources?.InformationMoreInfo}</span>
                        </div>
                    </div>

                    {/* birthday - booking type */}
                    {bookingType === "birthday" &&
                        <>
                            <div className="form-group row mt-4">
                                <label htmlFor="birthdayName" className="col-md-12 col-form-label">Name ﻿Geburtstagskind *</label>
                                <div className="col-md-12">
                                    <input
                                        type="text"
                                        name="birthdayName"
                                        className="form-control"
                                        value={buyerAddress?.birthdayName || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <label htmlFor="birthdayAge" className="col-md-12 col-form-label">Alter ﻿Geburtstagskind *</label>
                                <div className="col-md-12">
                                    <input
                                        type="text"
                                        name="birthdayAge"
                                        className="form-control"
                                        value={buyerAddress?.birthdayAge || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                        </>
                    }

                    {/* school group - booking type */}
                    {bookingType === "schoolgroup" &&
                        <div className="form-group row">
                            <label htmlFor="schoolGradeLevel" className="col-md-12 col-form-label">Klassenstufe *</label>
                            <div className="col-md-12">
                                <input
                                    type="text"
                                    name="schoolGradeLevel"
                                    className="form-control"
                                    value={buyerAddress?.schoolGradeLevel || ""}
                                    onChange={onInputChange}
                                />
                            </div>
                        </div>
                    }

                    {/* group - booking type */}
                    {bookingType === "groupvisit" &&
                        <div className="form-group row">
                            <label htmlFor="groupDescription" className="col-md-12 col-form-label">Beschreibung der Gruppe *</label>
                            <div className="col-md-12">
                                <textarea
                                    style={{ height: "100%" }}
                                    name="groupDescription"
                                    className="form-control"
                                    value={buyerAddress?.groupDescription || ""}
                                    onChange={onInputChange}
                                    rows={4}
                                />
                            </div>
                        </div>
                    }

                    <div className="form-group row">
                        <label htmlFor="remark" className="col-md-12 col-form-label">Bemerkung</label>
                        <div className="col-md-12">
                            <textarea
                                style={{ height: "100%" }}
                                name="remark"
                                className="form-control"
                                value={buyerAddress?.remark || ""}
                                onChange={onInputChange}
                                rows={4}
                            />
                        </div>
                    </div>


                    {/* button */}
                    <div className="row mt-5">
                        <div className="col-6">
                            <button
                                className="btn-custom white"
                                type="submit"
                                onClick={() => navigate(-1)}
                            >
                                {resources?._Back}
                            </button>
                        </div>

                        <div className="col-6 text-end">
                            <button
                                className="btn-custom"
                                type="submit"
                                onClick={handleUpdateBuyerAddress}
                            >
                                {resources?._Next}
                            </button>

                            {formError &&
                                <div className="row text-end mt-2">
                                    <p className="text-red">{formError}</p>
                                </div>
                            }
                        </div>
                    </div>
                </div>

            </div>

            <Footer language={language} />
        </>
    )
}

export default Information;